import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { GoogleOAuthProvider } from '@react-oauth/google'
import './index.css'
import App from './App.tsx'
import { logger } from './utils/logger'
import { googleClientId, isDevelopment } from './config/env'
import ErrorBoundary from './components/ErrorBoundary'

// Initialize Sentry (gracefully handle if not available)
let sentryInitialized = false;
if (!isDevelopment) {
  try {
    import('./lib/sentry').then(({ initializeSentry, addSentryBreadcrumb }) => {
      initializeSentry();
      sentryInitialized = true;
      
      logger.info('Sentry monitoring initialized', {
        component: 'Main',
        metadata: { environment: 'production' }
      });

      // Add application startup breadcrumb
      addSentryBreadcrumb(
        'Application starting up',
        'navigation',
        'info',
        {
          timestamp: new Date().toISOString(),
          environment: 'production'
        }
      );
    }).catch(() => {
      logger.warn('Sentry monitoring not available', {
        component: 'Main',
        metadata: { fallback: 'using local logging only' }
      });
    });
  } catch {
    logger.warn('Sentry initialization failed', {
      component: 'Main',
      metadata: { fallback: 'using local logging only' }
    });
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: true,
      cacheTime: 1000 * 60 * 5, // 5 minutes
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
      onError: (error) => {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        logger.error('React Query error occurred', errorObj, {
          component: 'ReactQuery',
          metadata: {
            errorType: errorObj.name,
            timestamp: new Date().toISOString()
          }
        });

        // Report React Query errors to Sentry if available
        if (sentryInitialized) {
          import('./lib/sentry').then(({ captureError }) => {
            captureError(errorObj, {
              source: 'ReactQuery',
              type: 'query_error'
            });
          }).catch(() => {
            // Silent fail for error reporting
          });
        }
      }
    },
  },
})

if (!googleClientId) {
  logger.warn('Google Client ID not configured', {
    component: 'Main',
    metadata: { 
      environment: isDevelopment ? 'development' : 'production',
      impact: 'Google Sign-In will not work'
    }
  });
}

// Initialize devtools in development
if (isDevelopment) {
  logger.info('Application starting in development mode', {
    component: 'Main',
    metadata: { devToolsEnabled: true }
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary 
      name="App"
      onError={(error, errorInfo) => {
        logger.error('Application-level error caught', error, {
          component: 'App-ErrorBoundary',
          metadata: {
            componentStack: errorInfo.componentStack,
            errorBoundaryLevel: 'application'
          }
        });
      }}
    >
      <GoogleOAuthProvider clientId={googleClientId || ''}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  </StrictMode>,
)
